import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

// import Custom Components
import PageHeader from "../../common/page-header";
import Breadcrumb from "../../common/breadcrumb";
import Accordion from "../../features/accordion/accordion";
import Card from "../../features/accordion/card";

import { isIEBrowser } from "../../../utils";

function FAQ() {
  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  handleScrollToTop();
  return (
    <div className="main">
      <Helmet>
        <title>LIBER - FAQ </title>
      </Helmet>

      <h1 className="d-none">LIBER - FAQ </h1>

      <PageHeader title="F.A.Q" subTitle="Pages" />
      <Breadcrumb title="FAQ" />

      <div className="page-content">
        <div className="container">
          <h2 className="title text-center mb-3">Shipping Information</h2>

          <Accordion adClass="accordion-rounded">
            <Card
              title="How will my parcel be delivered?"
              adClass="card-box card-sm bg-light"
            >
              Your parcel will be delivered through a shipping service. The
              specific details of the delivery process may vary depending on the
              shipping provider and the shipping method chosen. Typically, the
              parcel will be picked up from the sender's location, transported
              to a distribution center, sorted, and then delivered to the
              recipient's address.
            </Card>

            <Card
              title="Do I pay for delivery?"
              adClass="card-box card-sm bg-light"
            >
              Whether or not you have to pay for delivery depends on the
              specific terms and conditions of your purchase. Some online
              retailers offer free shipping as part of their promotions or for
              orders that meet certain criteria such as a minimum purchase
              amount. In other cases, you may be required to pay a shipping fee.
              It's best to review the information provided during the checkout
              process or contact the retailer directly to confirm if there are
              any charges for delivery.
            </Card>

            <Card
              title="Will I be charged customs fees?"
              adClass="card-box card-sm bg-light"
            >
              The potential for customs fees depends on the nature of your
              shipment and the customs regulations of the destination country.
              When shipping internationally, customs authorities may impose
              fees, duties, or taxes on imported goods. These charges are
              typically the responsibility of the recipient and are not included
              in the initial purchase price or shipping fees. It's advisable to
              research and understand the customs regulations of your country to
              anticipate any potential fees that may apply.
            </Card>

            <Card
              title="My item has become faulty"
              adClass="card-box card-sm bg-light"
            >
              If your item has become faulty, you may be eligible for a return,
              repair, or warranty service depending on the specific
              circumstances and the policies of the retailer or manufacturer.
              It's recommended to review the warranty or return policy provided
              by the seller or manufacturer to understand the options available
              to you. In some cases, you may need to contact customer support or
              follow a specific process to initiate a return or request
              assistance with the faulty item.
            </Card>
          </Accordion>

          <h2 className="title text-center mb-3">Orders and Returns</h2>

          <Accordion adClass="accordion-rounded">
            <Card title="Tracking my order" adClass="card-box card-sm bg-light">
              To track your order, you can typically use the tracking number
              provided by the seller or shipping carrier. This tracking number
              allows you to monitor the progress of your shipment and see the
              estimated delivery date. You can usually track your order online
              through the website of the shipping carrier or by using their
              tracking services. If you have any issues or questions regarding
              tracking your order, you can reach out to the seller or shipping
              carrier for assistance.
            </Card>

            <Card
              title="I haven’t received my order"
              adClass="card-box card-sm bg-light"
            >
              If you haven't received your order within the expected timeframe,
              there could be various reasons for the delay. First, check the
              tracking information for any updates or delivery attempts. It's
              possible that the delivery was attempted but unsuccessful, or
              there might be an issue with the address provided. If there are no
              updates or the tracking information doesn't provide clarity, it's
              best to contact the seller or the customer support of the online
              retailer to inquire about the status of your order and request
              assistance.
            </Card>

            <Card
              title="How can I return an item?"
              adClass="card-box card-sm bg-light"
            >
              The return process may vary depending on the seller, retailer, or
              the specific return policy in place. Typically, you can initiate a
              return by contacting the seller or accessing your account on the
              retailer's website. Look for the "Returns" or "Order History"
              section, where you should find instructions on how to initiate a
              return, print a return label, or request a return authorization.
              It's important to review the return policy of the seller or
              retailer to understand any specific requirements, such as return
              deadlines, item condition, or potential restocking fees.
            </Card>
          </Accordion>

          <h2 className="title text-center mb-3">Payments</h2>

          <Accordion adClass="accordion-rounded">
            <Card
              title="What payment types can I use?"
              adClass="card-box card-sm bg-light"
            >
              The accepted payment types can vary depending on the retailer or
              online store. However, commonly accepted payment methods include
              credit cards (such as Visa, Mastercard, American Express), debit
              cards, PayPal. During the checkout process, the available payment
              options should be displayed. If you have any specific payment
              method inquiries, it's best to check the website or contact the
              retailer's customer support for clarification.
            </Card>

            <Card
              title="Can I pay by Gift Card?"
              adClass="card-box card-sm bg-light"
            >
              Whether or not you can pay by Gift Card depends on the policies
              and capabilities of the retailer or online store. Some merchants
              accept gift cards as a valid form of payment, while others may
              have restrictions or limitations. During the checkout process,
              look for an option to enter your Gift Card information or consult
              the payment methods section on the retailer's website for specific
              details regarding the acceptance of Gift Cards.
            </Card>

            <Card
              title="I can't make a payment"
              adClass="card-box card-sm bg-light"
            >
              If you're experiencing difficulties making a payment, there could
              be several factors contributing to the issue. First, ensure that
              you have entered the correct payment information, including the
              card number, expiration date, security code, and billing address.
              If the problem persists, check your available funds or credit
              limit to ensure you have sufficient funds or credit available for
              the purchase. It's also recommended to try a different payment
              method or contact your card issuer or financial institution for
              assistance in case there are any restrictions or issues with your
              account.
            </Card>

            <Card
              title="Has my payment gone through?"
              adClass="card-box card-sm bg-light"
            >
              To confirm if your payment has gone through, you can check your
              bank statement or payment confirmation email. If the payment was
              successful, you should see a transaction record with the
              corresponding amount deducted from your account or a confirmation
              email indicating a successful payment. It may take some time for
              the payment to be processed and reflected in your account. If
              you're unsure or haven't received any confirmation, you can
              contact the retailer or check your order status on their website
              to verify if the payment was successfully processed.
            </Card>
          </Accordion>
        </div>
      </div>

      <div
        className="cta cta-display bg-image pt-4 pb-4"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/assets/images/backgrounds/cta/bg-7.jpg)`,
        }}
      >
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-10 col-lg-9 col-xl-7">
              <div
                className={`row no-gutters ${
                  isIEBrowser() ? "" : "flex-column"
                } flex-sm-row align-items-sm-center`}
              >
                <div className="col">
                  <h3 className="cta-title text-white">
                    If You Have More Questions
                  </h3>
                  <p className="cta-desc text-white">
                    Quisque volutpat mattis eros
                  </p>
                </div>

                <div className="col-auto">
                  <Link
                    to={`${process.env.PUBLIC_URL}/contact`}
                    className="btn btn-outline-white"
                  >
                    <span>CONTACT US</span>
                    <i className="icon-long-arrow-right"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default React.memo(FAQ);
